import { NumericKeyboard } from '@ps/isp-web-component-library';
import type { TrackingContextValue } from '@ps/web-shared-analytics';
import { TrackingCategory, buttonClick, panelToggle } from '@ps/web-shared-analytics';
import React, { useEffect, useMemo, useRef } from 'react';
import { useField, useFormState } from 'react-final-form';
import { FormattedNumber, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';

import type { FormData } from '../../../../types';
import { formatStakeInputValue } from '../../../../utils/formatStakeInputValue';
import { getStakeInputRegex } from '../../../../validation/getStakeInputRegex';

import { TXTCLI_Clear, TXTCLI_Undo, TXTWCL_Done } from './Keyboard.messages.json';
import styles from './Keyboard.scss';

const predefinedAmountsArray = [5, 10, 20];

const Keyboard: React.FC = () => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch<Dispatch>();
  const stakeCursorStartPosition = useSelector(select.betSlip.stakeCursorStartPosition);
  const stakeCursorEndPosition = useSelector(select.betSlip.stakeCursorEndPosition);
  const stakeInputDecimalSeparator = useSelector(select.betSlip.stakeInputDecimalSeparator);
  const isCustomKeyboardPredefinedAmountsEnabled = useSelector(
    select.betSlip.isCustomKeyboardPredefinedAmountsEnabled
  );
  const currency = useSelector(select.currency.get);
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);

  const hasUserOpenedKeyboard = useRef(false);
  const trackingContext: TrackingContextValue = useMemo(
    () => ({
      category: TrackingCategory.Sports,
      isMobileEmbedded
    }),
    [isMobileEmbedded]
  );
  const stakeInputRegex = useMemo(
    () => getStakeInputRegex(stakeInputDecimalSeparator),
    [stakeInputDecimalSeparator]
  );

  const { active } = useFormState<FormData>({
    subscription: { active: true }
  });
  const isStakeInputField = String(active).includes('stakePerLine');
  const { input } = useField<string, HTMLInputElement>(
    String(isStakeInputField ? active : undefined)
  );

  useEffect(() => {
    if (isStakeInputField) {
      hasUserOpenedKeyboard.current = true;
    }

    /**
     * We check the user has opened the keyboard because we don't
     * want to track "false" when it initially renders.
     */
    if (hasUserOpenedKeyboard.current) {
      panelToggle(
        {
          panelName: 'Sports_Custom_Keyboard',
          isOpen: isStakeInputField
        },
        trackingContext
      );
    }
  }, [isStakeInputField, trackingContext]);

  const createPredefinedAmountLabel = (amount: number) => (
    <>
      +{' '}
      <FormattedNumber
        value={amount}
        style="currency"
        currency={currency}
        minimumFractionDigits={0}
        maximumFractionDigits={0}
      />
    </>
  );

  const handleNumberPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();

    buttonClick(
      {
        buttonName: `Sports_Custom_Keyboard_${event.currentTarget.value}`
      },
      trackingContext
    );

    const buttonValueToBeInserted = event.currentTarget.value;
    const currentInputValue = String(input.value);

    const newInputValue =
      currentInputValue.slice(0, stakeCursorStartPosition) +
      buttonValueToBeInserted +
      currentInputValue.slice(stakeCursorEndPosition);

    const newInputFormattedValue = formatStakeInputValue({
      currentValue: newInputValue,
      validationRegex: stakeInputRegex,
      decimalSeparator: stakeInputDecimalSeparator
    });

    const isValid = stakeInputRegex.test(newInputFormattedValue);

    if (isValid && newInputFormattedValue !== currentInputValue) {
      input.onChange(newInputFormattedValue);

      const newCursorStartPosition = stakeCursorStartPosition + buttonValueToBeInserted.length;

      dispatch.betSlip.updateStakeCursorPosition({
        selectionStart: newCursorStartPosition,
        selectionEnd: newCursorStartPosition
      });
    }
  };

  const handleClearPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();

    buttonClick(
      {
        buttonName: 'Sports_Custom_Keyboard_Clear'
      },
      trackingContext
    );

    input.onChange('');
    dispatch.betSlip.updateStakeCursorPosition({ selectionStart: 0, selectionEnd: 0 });
  };

  const handleDecimalSeparatorPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();

    buttonClick(
      {
        buttonName: 'Sports_Custom_Keyboard_DecimalSeperator'
      },
      trackingContext
    );

    const currentInputValue = input.value;

    if (currentInputValue.includes(stakeInputDecimalSeparator)) {
      return;
    }

    const newInputValue =
      currentInputValue.slice(0, stakeCursorStartPosition) +
      stakeInputDecimalSeparator +
      currentInputValue.slice(stakeCursorEndPosition);

    const newInputFormattedValue = formatStakeInputValue({
      currentValue: newInputValue,
      validationRegex: stakeInputRegex,
      decimalSeparator: stakeInputDecimalSeparator
    });

    const isValid = stakeInputRegex.test(newInputFormattedValue);

    if (isValid) {
      input.onChange(newInputFormattedValue);
      dispatch.betSlip.updateStakeCursorPosition({
        selectionStart: stakeCursorStartPosition + 1,
        selectionEnd: stakeCursorStartPosition + 1
      });
    }
  };

  const handleBackspacePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();

    buttonClick(
      {
        buttonName: 'Sports_Custom_Keyboard_Undo'
      },
      trackingContext
    );

    if (input.value) {
      const selectionLength = stakeCursorEndPosition - stakeCursorStartPosition;

      let newCursorPosition: number;
      let newInputValue: string;

      if (selectionLength > 0) {
        newInputValue =
          input.value.slice(0, stakeCursorStartPosition) +
          input.value.slice(stakeCursorEndPosition);
        newCursorPosition = stakeCursorStartPosition;
      } else if (stakeCursorStartPosition > 0) {
        newInputValue =
          input.value.slice(0, stakeCursorStartPosition - 1) +
          input.value.slice(stakeCursorEndPosition);
        newCursorPosition = stakeCursorStartPosition - 1;
      } else {
        newInputValue = input.value;
        newCursorPosition = 0;
      }

      input.onChange(newInputValue);

      dispatch.betSlip.updateStakeCursorPosition({
        selectionStart: newCursorPosition,
        selectionEnd: newCursorPosition
      });
    }
  };

  const handleDonePointerDown = () => {
    buttonClick(
      {
        buttonName: 'Sports_Custom_Keyboard_Done'
      },
      trackingContext
    );

    input.onBlur();
  };

  /**
   * Fixes an issue for browsers on iOS, which causes the keyboard to close
   * when double clicking or clicking the keyboard's container.
   * These actions result in a "mouseDown" event, losing focus on the input,
   * which in turn closes the keyboard.
   */
  const handleMouseDown = (event: React.MouseEvent<HTMLElement>) => event.preventDefault();

  const handlePredefinedAmountDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    buttonClick(
      {
        buttonName: `Sports_Custom_Keyboard_${event.currentTarget.innerText}`
      },
      trackingContext
    );

    const inputValueNumber = parseFloat(input.value);
    const inputValueToCents = isNaN(inputValueNumber) ? 0 : inputValueNumber * 100;
    const predefinedAmountToCents = Number(event.currentTarget.value) * 100;
    const newInputValue = String((inputValueToCents + predefinedAmountToCents) / 100);
    const newCursorPosition = newInputValue.length;

    input.onChange(newInputValue);

    dispatch.betSlip.updateStakeCursorPosition({
      selectionStart: newCursorPosition,
      selectionEnd: newCursorPosition
    });
  };

  const predefinedAmounts = predefinedAmountsArray.map((amount) => ({
    amount,
    label: createPredefinedAmountLabel(amount)
  }));

  return (
    <NumericKeyboard
      data-testid="bet-slip-keyboard"
      className={styles.keyboard}
      isActive={isStakeInputField}
      decimalSeparator={stakeInputDecimalSeparator}
      doneText={formatMessage(TXTWCL_Done)}
      clearText={formatMessage(TXTCLI_Clear)}
      undoText={formatMessage(TXTCLI_Undo)}
      onHandleNumberPointerDown={handleNumberPointerDown}
      onHandleClearPointerDown={handleClearPointerDown}
      onHandleDecimalSeparatorPointerDown={handleDecimalSeparatorPointerDown}
      onHandleBackspacePointerDown={handleBackspacePointerDown}
      onHandleDonePointerDown={handleDonePointerDown}
      onMouseDown={handleMouseDown}
      predefinedAmounts={isCustomKeyboardPredefinedAmountsEnabled ? predefinedAmounts : undefined}
      onHandlePredefinedAmountDown={handlePredefinedAmountDown}
    />
  );
};

const KeyboardWrapper: React.FC = () => {
  const isCustomKeyboardEnabled = useSelector(select.betSlip.isCustomKeyboardEnabled);

  if (!isCustomKeyboardEnabled) {
    return null;
  }

  return <Keyboard />;
};

export default KeyboardWrapper;
